import { responseError } from '@/utils/errors';
import findResult, {
  assignIfExists,
  mapResults,
  findWithReadStatus,
  removeItemByCode,
  assignValue,
  findSelectedInCategory,
  assignProps,
  selectableResult,
} from '@/utils/resultsHelper';
import withDraft from '@/utils/draftHelper';
import { states, updateStates } from '@/constants/lifecycle';
import actions, {
  searchLists,
  searchLifeCycle,
} from '../constants';
import updateFacets from '../facetsHelper';
import {
  activeSearch,
  defaultSearch,
  pruneSearchErrors,
  checkAuto,
  foundResult,
  newSearchProperties,
  searchStateToLoad,
  findAllSearches,
  findSearch,
  findSearches,
} from './state';

const toggleSearchesSelected = withDraft((draft, action) => {
  const item = findSearches(draft, action);
  item.results.forEach((result) => {
    assignValue(result, 'selected', action.selected);
  });
});

const updateSearchesFilter = withDraft((draft, action) => {
  const item = findSearches(draft, action);
  item.filter = action.value;
});

const updateSearchesFlags = withDraft((draft, action) => {
  const item = findSearches(draft, action);
  item.currentPage = 1;
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.privateFlag = action.privateFlag;
  item.internalFlag = action.internalFlag;
});

const fetchSearches = withDraft((draft, action) => {
  const item = findSearches(draft, action);
  item.readStatus = states.PENDING;
  item.readErrors = [];
});

const showSearches = withDraft((draft, action) => {
  const item = findSearches(draft, action);
  item.readStatus = states.SUCCESS;
  item.readErrors = [];
  item.results = action.append
    ? mapResults(item.results, action, selectableResult)
    : mapResults([], action, selectableResult);
  item.pageSize = action.pageSize;
  item.count = action.resultsCount;
  item.currentPage = action.currentPage;
  item.totalPages = action.totalPages;
});

const showSearchesError = withDraft((draft, action) => {
  const item = findSearches(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const closeSearches = withDraft((draft, action) => {
  draft.searches[action.category] = searchStateToLoad({ search: draft }).searches[action.category];
});

const showDeleteSearches = withDraft((draft, action) => {
  draft.deleteSearchOpen = true;
  draft.searches.deletion.results = findSelectedInCategory(draft.searches, action.category);
});

const toggleSearchesPresentation = withDraft((draft, action) => {
  const item = findSearches(draft, action);
  item.display = action.display;
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.results = [];
  item.count = 0;
  item.currentPage = 1;
  item.totalPages = 0;
});

const editSearch = withDraft((draft, { code, result }) => {
  draft.activeSearch = activeSearch();
  findAllSearches(draft, { items: [{ code }] }).forEach((category) => {
    const item = findResult(draft.searches, category, code);
    assignIfExists(item, {
      ...result,
      ...{
        ...updateStates,
        isDirty: false,
      },
    });
  });
});

const updateSearchProperties = withDraft((draft, action) => {
  const item = findSearch(draft, action);
  assignIfExists(item, action.properties, searchLifeCycle, true);
});

const showSaveSearch = withDraft((draft, action) => {
  const item = findSearch(draft, action);
  assignIfExists(draft.activeSearch, newSearchProperties(item, action));
});

const toggleSearchSelected = withDraft((draft, action) => {
  const item = findSearch(draft, action);
  item.selected = !item.selected;
});

const refreshFind = withDraft((draft, action) => {
  const item = findSearch(draft, action);
  item.searchStatus = action.append ? states.APPENDING : states.PENDING;
  item.searchErrors = [];
  if (action.filter) {
    item.filter = action.filter;
  }
  if (action.query) {
    item.query = action.query;
  }
  if (action.index) {
    item.index = action.index;
  }
});

const showFindResults = withDraft((draft, action) => {
  const item = findSearch(draft, action);
  item.searchStatus = states.SUCCESS;
  item.searchErrors = [];
  item.results = action.append
    ? mapResults(item.results, action, foundResult) : mapResults([], action, foundResult);
  item.pageSize = action.pageSize;
  item.count = action.resultsCount;
  item.currentPage = action.currentPage;
  item.totalPages = action.totalPages;
  item.facets = action.facets
    ? updateFacets(item.facets, action.facets, item.filter, draft.searchConfig.config.facets) : {};
  item.lastEdited = '';
  item.info = action.info;
  item.warnings = action.warnings;
});

const showFindError = withDraft((draft, action) => {
  const item = findSearch(draft, action);
  item.results = [];
  item.count = 0;
  item.searchStatus = states.ERROR;
  item.searchErrors = [responseError(action)];
});

const fetchSearch = withDraft((draft, action) => {
  const item = findSearch(draft, action);
  if (item) {
    item.readStatus = states.PENDING;
    item.readErrors = [];
    if (action.auto) {
      item.searchStatus = states.PENDING;
      item.searchErrors = [];
    }
  } else {
    draft.searches[action.category].results.push(checkAuto(action));
  }
});

const showSearch = withDraft((draft, action) => {
  const item = findSearch(draft, action);
  assignProps(item, {
    ...action.result,
    results: [],
    count: 0,
    readStatus: states.SUCCESS,
    readErrors: [],
    isNew: !action.code,
    searchStatus: states.NONE,
  });
});

const showSearchError = withDraft((draft, action) => {
  const item = findSearch(draft, action);
  if (action.auto) {
    item.searchStatus = states.NONE;
  }
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const showDeleteSearch = withDraft((draft, action) => {
  const item = findSearch(draft, action);
  draft.deleteSearchOpen = true;
  draft.searches.deletion.results = [item];
});

const closeSearch = withDraft((draft, action) => {
  const item = findSearch(draft, action);
  if (item?.code) {
    draft.searches[action.category].results = removeItemByCode(draft.searches[action.category].results, item);
  } else if (item) {
    assignIfExists(item, defaultSearch());
    item.filter = {};
  }
});

const toggleOmniSearchFlags = withDraft((draft, action) => {
  const item = findSearch(draft, action);
  item.linksFlag = action.linksFlag;
  item.kbFlag = action.kbFlag;
  item.datasetsFlag = action.datasetsFlag;
});

const deleteSearch = withDraft((draft, action) => {
  findAllSearches(draft, action).forEach((category) => {
    action.items.forEach(({ code }) => {
      const item = findResult(draft.searches, category, code);
      if (item) {
        item.destroyStatus = states.PENDING;
        item.destroyErrors = [];
      }
    });
  });
});

const destroySearch = withDraft((draft, action) => {
  draft.deleteSearchOpen = false;
  findAllSearches(draft, action).forEach((category) => {
    action.items.forEach(({ code }) => {
      const item = findResult(draft.searches, category, code);
      if (item) {
        draft.searches[category].results = removeItemByCode(draft.searches[category].results, item);
      }
    });
  });
});

const destroySearchError = withDraft((draft, action) => {
  draft.deleteSearchOpen = false;
  findAllSearches(draft, action).forEach((category) => {
    action.items.forEach(({ code, error }) => {
      const item = findResult(draft.searches, category, code);
      if (item) {
        item.destroyStatus = states.ERROR;
        item.destroyErrors = [error];
      }
    });
  });
});

const newSearch = withDraft((draft) => {
  assignIfExists(
    draft.activeSearch,
    { ...activeSearch(), createStatus: states.SUCCESS },
  );
  findWithReadStatus(draft.searches, searchLists, states.SUCCESS).forEach((category) => {
    draft.searches[category].readStatus = states.PENDING;
    draft.searches[category].readErrors = [];
  });
});

const hideSaveSearch = withDraft((draft) => { draft.activeSearch = activeSearch(); });

const hideDeleteSearch = withDraft((draft) => {
  draft.deleteSearchOpen = false;
  draft.searches.deletion.results = [];
});

const createSearch = withDraft((draft) => { draft.activeSearch.createStatus = states.PENDING; });

const updateActiveSearchProperties = withDraft((draft, action) => {
  assignIfExists(draft.activeSearch, action.properties);
  if (draft.activeSearch.isNew) {
    draft.activeSearch.createErrors = pruneSearchErrors(draft.activeSearch.createErrors, action);
  } else {
    draft.activeSearch.updateErrors = pruneSearchErrors(draft.activeSearch.updateErrors, action);
  }
});

const newSearchError = withDraft((draft, action) => {
  assignIfExists(draft.activeSearch, {
    createStatus: states.ERROR,
    createErrors: [responseError(action)],
  });
});

const editSearchError = withDraft((draft, action) => {
  assignIfExists(draft.activeSearch, {
    updateStatus: states.ERROR,
    updateErrors: [responseError(action)],
  });
});

const fetchSearchConfig = withDraft((draft) => { draft.searchConfig.readStatus = states.PENDING; });

const showSearchConfig = withDraft((draft, action) => {
  draft.searchConfig.readStatus = states.SUCCESS;
  draft.searchConfig.config = action.searchConfig;
});

const showSearchConfigError = withDraft((draft, action) => {
  assignIfExists(draft.searchConfig, {
    updateStatus: states.ERROR,
    updateErrors: [responseError(action)],
  });
});

export default {
  [actions.CLOSE_SEARCH]: closeSearch,
  [actions.CLOSE_SEARCHES]: closeSearches,
  [actions.CREATE_SEARCH]: createSearch,
  [actions.DELETE_SEARCH]: deleteSearch,
  [actions.DESTROY_SEARCH]: destroySearch,
  [actions.DESTROY_SEARCH_ERROR]: destroySearchError,
  [actions.EDIT_SEARCH]: editSearch,
  [actions.EDIT_SEARCH_ERROR]: editSearchError,
  [actions.FETCH_SEARCH]: fetchSearch,
  [actions.FETCH_SEARCH_CONFIG]: fetchSearchConfig,
  [actions.FETCH_SEARCHES]: fetchSearches,
  [actions.FIND]: refreshFind,
  [actions.HIDE_DELETE_SEARCH]: hideDeleteSearch,
  [actions.HIDE_SAVE_SEARCH]: hideSaveSearch,
  [actions.NEW_SEARCH]: newSearch,
  [actions.NEW_SEARCH_ERROR]: newSearchError,
  [actions.REFRESH_FIND]: refreshFind,
  [actions.REFRESH_SEARCHES]: fetchSearches,
  [actions.SHOW_DELETE_SEARCH]: showDeleteSearch,
  [actions.SHOW_DELETE_SEARCHES]: showDeleteSearches,
  [actions.SHOW_FIND_ERROR]: showFindError,
  [actions.SHOW_FIND_RESULTS]: showFindResults,
  [actions.SHOW_SAVE_SEARCH]: showSaveSearch,
  [actions.SHOW_SEARCH]: showSearch,
  [actions.SHOW_SEARCH_CONFIG]: showSearchConfig,
  [actions.SHOW_SEARCH_CONFIG_ERROR]: showSearchConfigError,
  [actions.SHOW_SEARCH_ERROR]: showSearchError,
  [actions.SHOW_SEARCHES]: showSearches,
  [actions.SHOW_SEARCHES_ERROR]: showSearchesError,
  [actions.TOGGLE_OMNI_SEARCH_FLAGS]: toggleOmniSearchFlags,
  [actions.TOGGLE_SEARCH_SELECTED]: toggleSearchSelected,
  [actions.TOGGLE_SEARCHES_PRESENTATION]: toggleSearchesPresentation,
  [actions.TOGGLE_SEARCHES_SELECTED]: toggleSearchesSelected,
  [actions.UPDATE_ACTIVE_SEARCH_PROPERTIES]: updateActiveSearchProperties,
  [actions.UPDATE_SEARCH_PROPERTIES]: updateSearchProperties,
  [actions.UPDATE_SEARCHES_FILTER]: updateSearchesFilter,
  [actions.UPDATE_SEARCHES_FLAGS]: updateSearchesFlags,
};
